import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getSurveySectionAnswers,
  SurveyQITAnswer,
} from 'src/api/dataVault/survey';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface Entry {
  status: Status;
  rows: SurveyQITAnswer[];
  error: string | null;
  lastFetchedAt: number | null;
}

interface State {
  byKey: Record<string, Entry>;
}

const STALE_MS = 30_000;
const initialState: State = { byKey: {} };

function makeKey(surveyId: number, sectionId: string | number): string {
  return `${surveyId}:${sectionId}`;
}

export const fetchSurveySectionAnswers = createAsyncThunk(
  'surveySectionDetail/fetch',
  async (
    {
      surveyId,
      sectionId,
      actingOnMemberId,
      forceRefresh = false,
    }: {
      surveyId: number;
      sectionId: string | number;
      actingOnMemberId?: number | null;
      forceRefresh?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    const state = (getState() as { surveySectionDetail: State })
      .surveySectionDetail;
    const key = makeKey(surveyId, sectionId);
    const entry = state.byKey[key];
    if (
      !forceRefresh &&
      entry?.status === 'succeeded' &&
      entry.lastFetchedAt &&
      Date.now() - entry.lastFetchedAt < STALE_MS
    ) {
      return null;
    }
    try {
      return await getSurveySectionAnswers(surveyId, sectionId, {
        actingOnMemberId,
      });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load section answers'
      );
    }
  }
);

const slice = createSlice({
  name: 'surveySectionDetail',
  initialState,
  reducers: {
    invalidateSection(
      state,
      action: PayloadAction<{ surveyId: number; sectionId: string | number }>
    ) {
      delete state.byKey[
        makeKey(action.payload.surveyId, action.payload.sectionId)
      ];
    },
    clearAllForSurvey(state, action: PayloadAction<number>) {
      const prefix = `${action.payload}:`;
      Object.keys(state.byKey).forEach((k) => {
        if (k.startsWith(prefix)) {
          delete state.byKey[k];
        }
      });
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSurveySectionAnswers.pending, (state, action) => {
        const key = makeKey(
          action.meta.arg.surveyId,
          action.meta.arg.sectionId
        );
        state.byKey[key] = {
          status: 'loading',
          rows: [],
          error: null,
          lastFetchedAt: null,
        };
      })
      .addCase(fetchSurveySectionAnswers.fulfilled, (state, action) => {
        const key = makeKey(
          action.meta.arg.surveyId,
          action.meta.arg.sectionId
        );
        if (action.payload === null) {
          return;
        }
        state.byKey[key] = {
          status: 'succeeded',
          rows: action.payload,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchSurveySectionAnswers.rejected, (state, action) => {
        const key = makeKey(
          action.meta.arg.surveyId,
          action.meta.arg.sectionId
        );
        state.byKey[key] = {
          status: 'failed',
          rows: [],
          error: action.payload as string,
          lastFetchedAt: null,
        };
      });
  },
});

export const { invalidateSection, clearAllForSurvey } = slice.actions;

export const selectSurveySectionAnswers =
  (surveyId: number, sectionId: string | number) =>
  (state: { surveySectionDetail: State }): Entry | undefined =>
    state.surveySectionDetail.byKey[makeKey(surveyId, sectionId)];

export default slice.reducer;
